import {
  Avatar,
  AvatarFallback,
  BreadcrumbEllipsis,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@folio/components';
import { hasBusinessRole } from '@folio/utils/hasBusinessRole';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
} from 'korint-sdk';
import { useKorintConfig, useSignOut } from 'korint-sdk/react';
import { HelpCircleIcon, LogOutIcon, SparklesIcon, UserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { mutate } from 'swr';
import { clearMfaSetupSkip } from '../../features/auth/mfaSetupSkip';
import { Group } from '@folio/components/ui/flex';

// Disgusting way to clear the cache. https://swr.vercel.app/docs/mutation.en-US#mutate-multiple-items
const clearCache = () => mutate(() => true, undefined, { revalidate: false });

export function UserSidebarFooter() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { korintConfig } = useKorintConfig();

  const { signOut } = useSignOut({
    onSuccess: () => {
      navigate('/auth/sign-in');
      clearCache();
      queryClient.clear();
      clearMfaSetupSkip();
    },
  });
  const { data } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });
  const queryClient = useQueryClient();

  const brokerageFirmId = data?.brokerageFirmId;
  const { data: firmType } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId!),
    enabled: !!brokerageFirmId,
    select: brokerageFirm => brokerageFirm.firmType,
  });

  const { supportLinkRoles } = korintConfig;
  const showSupportLink = !supportLinkRoles || hasBusinessRole(data, supportLinkRoles);
  const showNewFeaturesLink = firmType === 'WHOLESALE';

  return (
    <Group gap={0}>
      <Group
        gap={0}
        className="text-sm overflow-hidden whitespace-nowrap text-ellipsis"
        title={data?.email}
      >
        <Avatar className="mr-4">
          <AvatarFallback
            delayMs={0}
            className="mx-auto my-auto font-medium text-gray-600 bg-gray-100"
          >
            {data?.email?.[0]?.toLocaleUpperCase()}
          </AvatarFallback>
        </Avatar>
        {data?.email}
      </Group>
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center ml-auto p-1 hover:bg-gray-50 rounded">
          <BreadcrumbEllipsis />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel className="text-gray-400 font-normal text-xs">
            {data?.email}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-gray-400 font-normal text-xs">
            {t('userSidebarFooter.myAccount')}
          </DropdownMenuLabel>
          {showSupportLink && (
            <DropdownMenuItem
              className="font-medium flex items-center justify-between gap-2"
              onClick={() => window.open('https://support.korint.io/', '_blank')}
            >
              <HelpCircleIcon className="text-gray-400 h-4" />
              {t('userSidebarFooter.support')}
            </DropdownMenuItem>
          )}
          {showNewFeaturesLink && (
            <DropdownMenuItem
              className="font-medium flex items-center justify-between gap-2"
              onClick={() => window.open('https://korint.notion.site/nouveautes', '_blank')}
            >
              <SparklesIcon className="text-gray-400 h-4" />
              {t('userSidebarFooter.newFeatures')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className="font-medium flex items-center justify-between gap-2"
            onClick={() => {
              navigate('/redirect');
            }}
          >
            <UserIcon className="text-gray-400 h-4" />
            {t('userSidebarFooter.switchProfile')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="font-medium flex items-center justify-between gap-2"
            onClick={() => {
              signOut();
            }}
          >
            <LogOutIcon className="text-gray-400 h-4" /> {t('userSidebarFooter.signOut')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </Group>
  );
}
